import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Megaphone } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = '__all__';

export const AnnouncementsPanel = ({ token, onUnauthorized, refreshKey }) => {
  const [message, setMessage] = useState('');
  const [place, setPlace] = useState(ALL);
  const [places, setPlaces] = useState([]);
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const fail = useCallback((e) => { if (e?.response?.status === 401) onUnauthorized(); }, [onUnauthorized]);

  const loadHistory = useCallback(() => {
    axios.get(`${API}/admin/announcements`, auth()).then((r) => setHistory(r.data)).catch(fail);
  }, [auth, fail]);

  useEffect(() => {
    axios.get(`${API}/admin/announcements/places`, auth()).then((r) => setPlaces(r.data)).catch(fail);
    loadHistory();
  }, [auth, fail, loadHistory, refreshKey]);

  useEffect(() => {
    if (!history.some((h) => h.status === 'sending')) return undefined;
    const id = setTimeout(loadHistory, 3000);
    return () => clearTimeout(id);
  }, [history, loadHistory]);

  const total = places.reduce((s, p) => s + p.count, 0);
  const count = place === ALL ? total : (places.find((p) => p.place === place)?.count || 0);

  const send = async () => {
    if (!message.trim() || count === 0) return;
    if (!window.confirm(`Send this announcement to ${count} member(s)?`)) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/announcements`, { message: message.trim(), place: place === ALL ? null : place }, auth());
      setMessage('');
      loadHistory();
    } catch (e) {
      fail(e);
      if (e?.response?.status !== 401) window.alert(e?.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  return (
    <section className="jbss-visit-stats" data-testid="admin-announcements">
      <div className="jbss-announce-box">
        <Label htmlFor="ann-msg" className="jbss-admin-label">Message</Label>
        <Textarea id="ann-msg" rows={4} maxLength={1500} value={message} onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. Ambedkar Jayanti programme on 14 April, 10 AM at Town Hall. All members are invited." data-testid="admin-announcement-message" />
        <div className="jbss-export-row">
          <div>
            <Label className="jbss-admin-label">Send to</Label>
            <Select value={place} onValueChange={setPlace}>
              <SelectTrigger className="jbss-admin-level" data-testid="admin-announcement-place"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All places ({total})</SelectItem>
                {places.map((p) => <SelectItem key={p.place} value={p.place}>{p.place} ({p.count})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button className="jbss-admin-add" onClick={send} disabled={sending || !message.trim() || count === 0} data-testid="admin-announcement-send">
            {sending ? <Loader2 className="jbss-spin" size={15} /> : <Megaphone size={15} />} Send to {count} member{count === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
      {history.length > 0 && (
        <div className="jbss-admin-table jbss-donation-list" data-testid="admin-announcement-history">
          {history.map((h) => (
            <div className="jbss-admin-trow" key={h.id} data-testid={`admin-announcement-${h.id}`}>
              <div className="jbss-admin-tinfo">
                <span className="jbss-admin-tname jbss-app-info">{h.message}</span>
                <span className="jbss-admin-tmeta">
                  {new Date(h.created_at).toLocaleString('en-IN')} · {h.place || 'All places'} · {h.recipients} recipients ·
                  {' '}{h.status === 'sending' ? 'Sending…' : h.status === 'interrupted' ? 'Interrupted' : 'Done'} · Sent {h.sent} · Failed {h.failed}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
